import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { API_BASE } from "../lib/api";

const INITIAL = {
  role: "assistant",
  content:
    "Hey — I'm Coach Kaf. Ask me anything about our programs, becoming a mentor or mentee, donating, or upcoming events. I got you.",
};

function makeSessionId() {
  return "chat_" + Math.random().toString(36).slice(2, 14);
}

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([INITIAL]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sessionIdRef = useRef(makeSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-kaf-chat", handler);
    return () => window.removeEventListener("open-kaf-chat", handler);
  }, []);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setSending(true);

    try {
      const res = await fetch(`${API_BASE}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionIdRef.current, message: text }),
      });
      if (!res.body) throw new Error("No stream body");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const chunk = p.slice(6);
          if (chunk === "[DONE]") continue;
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
            return copy;
          });
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry — I couldn't reach the assistant just now. Please try again." };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="chatbot-open-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 group flex items-center gap-3 rounded-full bg-[#6B21A8] py-2 pl-2 pr-5 text-white shadow-2xl transition-transform hover:-translate-y-0.5 hover:bg-[#7B2AB8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F5B90A]"
          aria-label="Chat with Coach Kaf"
        >
          <span className="relative flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-white ring-2 ring-[#F5B90A]">
            <img src="/coach-kaf-avatar.png" alt="Coach Kaf" className="h-14 w-auto -translate-y-1 object-cover" />
          </span>
          <span className="hidden sm:flex flex-col text-left leading-tight">
            <span className="text-[10px] uppercase tracking-widest text-[#F5B90A]">Ask</span>
            <span className="font-semibold">Coach Kaf</span>
          </span>
        </button>
      )}

      {open && (
        <div data-testid="chatbot-panel" className="fixed bottom-6 right-6 z-50 flex h-[580px] w-[92vw] max-w-sm flex-col overflow-hidden rounded-lg border border-black/10 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-black/10 bg-[#0A0A0A] px-4 py-3 text-white">
            <div className="flex items-center gap-3">
              <span className="relative flex h-11 w-11 items-center justify-center overflow-hidden rounded-full bg-[#4A154B] ring-2 ring-[#F5B90A]">
                <img src="/coach-kaf-avatar.png" alt="Coach Kaf" className="h-full w-full object-cover object-top" />
              </span>
              <div>
                <p className="overline text-[#F5B90A] leading-none">Ask anything</p>
                <p className="font-display text-lg font-semibold leading-tight">Coach Kaf</p>
              </div>
            </div>
            <button data-testid="chatbot-close-btn" onClick={() => setOpen(false)} aria-label="Close chat" className="rounded p-1 hover:bg-white/10">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto bg-neutral-50 p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex items-end gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "assistant" && (
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#4A154B]">
                    <img src="/coach-kaf-avatar.png" alt="" className="h-full w-full object-cover object-top" />
                  </span>
                )}
                <div className={`max-w-[80%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm leading-relaxed ${
                  m.role === "user" ? "bg-[#6B21A8] text-white" : "border border-black/10 bg-white text-black"
                }`}>
                  {m.content || (sending && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin" /> : "")}
                </div>
              </div>
            ))}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-center gap-2 border-t border-black/10 bg-white p-3"
          >
            <input
              data-testid="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Coach Kaf…"
              className="flex-1 rounded-md border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:border-[#6B21A8]"
              disabled={sending}
            />
            <button
              data-testid="chatbot-send-btn"
              type="submit"
              disabled={sending || !input.trim()}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[#6B21A8] text-white transition-transform hover:-translate-y-0.5 disabled:opacity-50"
              aria-label="Send message"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
